import {NyButton, NyForm, NyModal, NyTextInput} from 'nyse-web-tools-common/lib/react';
import NySubmitBtn from 'nyse-web-tools-common/lib/react/form/NySubmitBtn/NySubmitBtn';
import connect from 'nyse-web-tools-common/lib/utils/redux.connect';
import {FieldArray, useFormikContext} from 'formik';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';

import {FirmParticipantSelect} from '../../selects/firmParticipant.select';
import {SymbolTypeaheadInput} from '../../input/SymbolTypeaheadInput';
import {ClearingNumberByMpidSelect} from '../../selects/clearingNumberByMpid.select';

export const MAX_ROWS = 25;

const FormFields = ({userCrd, isCreating, isEditMode, onClose, errorMessage}) => {
    const {values} = useFormikContext();

    return (
        <>
            <div className='modal-body' style={{minHeight: '300px', maxHeight: '70vh', overflowY: 'auto'}}>
                {errorMessage && (
                    <div className='alert alert-danger space-bottom-2'>
                        {errorMessage}
                    </div>
                )}
                <FieldArray name='rows'>
                    {({push, remove}) => (
                        <div>
                            <div className='space-bottom-2'>
                                {values.rows.map((row, index) => (
                                    <div key={index} className='row space-bottom-1'>
                                        <div className='col-md-3'>
                                            {index === 0 && <label>MPID</label>}
                                            <FirmParticipantSelect
                                                crd={userCrd}
                                                name={`rows[${index}].mpid`}
                                                disableInactiveControl
                                                hideEtpid
                                                required
                                                menuPortalTarget={document.body}
                                                menuPosition='fixed'
                                                disabled={isEditMode}
                                            />
                                        </div>
                                        <div className='col-md-4'>
                                            {index === 0 && <label>Symbol</label>}
                                            {isEditMode ? (
                                                <NyTextInput
                                                    name={`rows[${index}].symbol`}
                                                    maxLength={24}
                                                    readOnly
                                                    required
                                                />
                                            ) : (
                                                <SymbolTypeaheadInput
                                                    name={`rows[${index}].symbol`}
                                                    maxLength={24}
                                                    placeholder='Search symbol...'
                                                    required
                                                    limit={10}
                                                />
                                            )}
                                        </div>
                                        <div className='col-md-3'>
                                            {index === 0 && <label>Clearing Number</label>}
                                            <ClearingNumberByMpidSelect
                                                crd={userCrd}
                                                mpidFieldName={`rows[${index}].mpid`}
                                                name={`rows[${index}].clearingNumber`}
                                                required
                                            />
                                        </div>
                                        <div className='col-md-2'>
                                            {index === 0 && <label>&nbsp;</label>}
                                            {!isEditMode && index > 0 && (
                                                <NyButton
                                                    danger
                                                    small
                                                    onClick={() => remove(index)}
                                                    disabled={isCreating}>
                                                    Remove
                                                </NyButton>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {!isEditMode && (
                                <div className='row'>
                                    <div className='col-md-12'>
                                        <button
                                            type='button'
                                            className='btn btn-info'
                                            onClick={() => push({mpid: '', symbol: '', clearingNumber: ''})}
                                            disabled={isCreating || values.rows.length >= MAX_ROWS}>
                                            Add More
                                        </button>
                                        {values.rows.length >= MAX_ROWS && (
                                            <span className='text-muted small space-left-1'>
                                                Maximum {MAX_ROWS} rows reached
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </FieldArray>
            </div>

            <div className='modal-footer'>
                <NySubmitBtn disabled={isCreating} className='space-right-1'>
                    {isCreating ? 'Saving...' : 'Save'}
                </NySubmitBtn>
                <NyButton
                    primary
                    onClick={onClose}
                    disabled={isCreating}>
                    Cancel
                </NyButton>
            </div>
        </>
    );
};

const AddUpdateSymbolMpidClearingModalComponent = (props) => {
    const {
        userCrd,
        onClose,
        symbolMpidClearing,
        isEditMode = false,
        existingRows = [],
        show,
        hideModal,
        createSymbolMpidClearing
    } = props;

    const isMountedRef = useRef(false);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const isCreating = symbolMpidClearing.isCreating;

    const initialRows = isEditMode && existingRows.length > 0
        ? existingRows.map((row) => ({
            mpid: row.mpid || '',
            symbol: row.symbol || '',
            clearingNumber: row.clearingNumber || ''
        }))
        : [{mpid: '', symbol: '', clearingNumber: ''}];

    const initialValues = {
        rows: initialRows.map((row) => ({
            mpid: row.mpid,
            symbol: row.symbol,
            clearingNumber: row.clearingNumber
        }))
    };

    const validate = (values) => {
        const errors = {rows: []};
        let hasError = false;

        values.rows.forEach((row, index) => {
            const rowErrors = {};

            if (!row.mpid || row.mpid.trim() === '') {
                rowErrors.mpid = 'MPID is required';
                hasError = true;
            }

            if (!row.symbol || row.symbol.trim() === '') {
                rowErrors.symbol = 'Symbol is required';
                hasError = true;
            } else if (row.symbol.length > 24) {
                rowErrors.symbol = 'Symbol must not exceed 24 characters';
                hasError = true;
            }

            if (!row.clearingNumber || row.clearingNumber.trim() === '') {
                rowErrors.clearingNumber = 'Clearing Number is required';
                hasError = true;
            } else if (row.clearingNumber.length > 5) {
                rowErrors.clearingNumber = 'Clearing Number must not exceed 5 characters';
                hasError = true;
            }

            errors.rows[index] = rowErrors;
        });

        return hasError ? errors : {};
    };

    const onSubmit = async (values) => {
        // Clear any previous error messages
        if (isMountedRef.current) {
            setErrorMessage(null);
        }

        const configurations = values.rows
            .filter((row) => row.mpid && row.symbol && row.clearingNumber)
            .map((row) => ({
                mpid: row.mpid.trim(),
                symbol: row.symbol.trim().toUpperCase(),
                clearingNumber: row.clearingNumber.trim()
            }));

        if (configurations.length === 0) {
            return;
        }

        try {
            const action = await createSymbolMpidClearing({userCrd, configurations});
            // Extract success message from API response
            const successMessage = action?.payload?.data?.data || 'Symbol MPID clearing configuration(s) saved successfully';

            // Delay closing to allow Formik to complete its state updates
            setTimeout(() => {
                if (isMountedRef.current) {
                    onClose(true, successMessage);
                }
            }, 0);
        } catch (error) {
            const errorMsg = error?.error?.detail
                || error?.error?.title
                || error?.error?.message
                || error?.message
                || 'Failed to save configurations. Please try again.';

            if (isMountedRef.current) {
                setErrorMessage(errorMsg);
            }
        }
    };

    return (
        <NyModal
            show={show}
            hideModal={hideModal}
            title={isEditMode ? 'Update Mappings' : 'Add New Mappings'}
            className='modal-medium'>
            <NyForm
                initialValues={initialValues}
                validate={validate}
                onSubmit={onSubmit}>
                <FormFields
                    userCrd={userCrd}
                    isCreating={isCreating}
                    isEditMode={isEditMode}
                    onClose={() => onClose(false)}
                    errorMessage={errorMessage}
                />
            </NyForm>
        </NyModal>
    );
};

AddUpdateSymbolMpidClearingModalComponent.propTypes = {
    userCrd: PropTypes.number.isRequired,
    firms: PropTypes.array.isRequired,
    isEditMode: PropTypes.bool,
    existingRows: PropTypes.array,
    show: PropTypes.bool.isRequired,
    hideModal: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    createSymbolMpidClearing: PropTypes.func.isRequired,
    symbolMpidClearing: PropTypes.shape({
        isCreating: PropTypes.bool,
        createError: PropTypes.object
    })
};

export const AddUpdateSymbolMpidClearingModal = connect(['symbolMpidClearing'])(
    AddUpdateSymbolMpidClearingModalComponent
);
